import type { Selection } from '@atlaskit/editor-prosemirror/state';

import type { EligibleRange } from './types';

const MIN_SENTENCE_COUNT = 3;
const MIN_CHARACTER_COUNT = 150;

// Regex to check if text ends with a sentence-ending punctuation mark.
// Supports Latin (.!?) and CJK (。！？) end marks, with optional trailing whitespace.
// eslint-disable-next-line require-unicode-regexp
const END_MARK_REGEX = /[.!?\u3002\uFF01\uFF1F]\s*$/;

/**
 * Count the number of sentences in the given text.
 *
 * Primary: uses Intl.Segmenter with 'sentence' granularity (handles Latin, CJK, and other scripts).
 * Fallback: uses a regex for environments without Intl.Segmenter support.
 */
export function countSentences({ text, locale }: { locale?: string; text: string }): number {
	if (!text) {
		return 0;
	}

	if (typeof Intl !== 'undefined' && Intl.Segmenter) {
		const segmenter = new Intl.Segmenter(locale ?? 'en', {
			granularity: 'sentence',
		});
		const segments = Array.from(segmenter.segment(text));
		return segments.length;
	}

	// Fallback: regex matching sentence-ending punctuation
	// Latin punctuation requires following whitespace or end-of-string.
	// CJK punctuation (。！？) can appear without trailing whitespace.
	// eslint-disable-next-line require-unicode-regexp
	const sentenceEndRegex = /[.!?](?:\s|$)|[\u3002\uFF01\uFF1F]/g;
	const matches = text.match(sentenceEndRegex);
	return matches ? matches.length : 1;
}

/**
 * Check if the content at the cursor position is eligible
 * for the AI quick prompt.
 *
 * Eligibility requires:
 * - The cursor is at the end of the node
 * - The content ends with a sentence-ending punctuation mark (.!? or CJK equivalents)
 * - The content of the node has >= 3 sentences OR >= 150 characters
 *
 * @returns The node position range if eligible, null otherwise
 */
export function checkContentEligibility({
	locale,
	selection,
}: {
	locale?: string;
	selection: Selection;
}): EligibleRange | null {
	const { $to } = selection;

	// Find the node at cursor depth
	const depth = $to.depth;
	const parentNode = $to.node(depth);

	// Only support paragraph for now
	// we can add support for other textblock nodes later if needed by using isTextblock.
	if (parentNode.type.name !== 'paragraph') {
		return null;
	}

	// If the selection is not at the end of the node, return null
	if (selection.from !== selection.to || selection.from !== $to.end(depth)) {
		return null;
	}

	const text = parentNode.textContent;

	// The paragraph must end with a sentence-ending punctuation mark
	if (!END_MARK_REGEX.test(text)) {
		return null;
	}

	// Count sentences
	const sentenceCount = countSentences({ text, locale });

	// Check thresholds: >= 3 sentences OR >= 150 characters (fallback)
	// The character-count fallback covers cases where sentence counting undercounts
	// (e.g., long run-on sentences, or scripts with non-standard punctuation).
	if (sentenceCount < MIN_SENTENCE_COUNT && text.length < MIN_CHARACTER_COUNT) {
		return null;
	}

	// Return the absolute position range of the node
	const from = $to.start(depth);
	const to = $to.end(depth);
	return { from, to };
}
